import { apiClient } from './api-client';

export interface ActivityLog {
  id: number;
  userId?: number;
  action: string;
  entityType: string;
  entityId: string;
  details?: string;
  ipAddress?: string;
  userAgent?: string;
  createdAt: string;
}

export interface LogFilters {
  /** Substring match on request path (from observability payload `url`). */
  pathContains?: string;
  /** HTTP method, e.g. GET, POST. */
  method?: string;
  /** Authenticated user id from `payload.activity` (when route used auth middleware). */
  userId?: number;
  correlationId?: string;
  startDate?: string;
  endDate?: string;
  page?: number;
  limit?: number;
}

export type ActivityLogsPagination = {
  page: number;
  limit: number;
  total: number;
  lastPage: number;
};

/** Structured business rows from `activity_logs` (ActivityLogService). */
export interface BusinessActivityLog {
  id: number;
  activityType: string;
  module: string;
  entityId: string;
  entityType: string;
  userId: number | null;
  correlationId: string;
  description: string;
  metadata: Record<string, unknown>;
  severity: string;
  createdAt: string;
}

export interface BusinessLogFilters {
  module?: string;
  activityType?: string;
  entityId?: string;
  userId?: number;
  correlationId?: string;
  severity?: string;
  startDate?: string;
  endDate?: string;
  page?: number;
  limit?: number;
}

class LogsService {
  async getActivityLogs(filters?: LogFilters): Promise<{
    success: boolean;
    data: ActivityLog[];
    pagination: ActivityLogsPagination;
    message?: string;
  }> {
    const q = new URLSearchParams();
    if (filters?.page) q.set('page', String(filters.page));
    if (filters?.limit) q.set('limit', String(filters.limit));
    if (filters?.pathContains?.trim()) q.set('path', filters.pathContains.trim());
    if (filters?.method?.trim()) q.set('method', filters.method.trim());
    if (filters?.correlationId?.trim()) q.set('correlation_id', filters.correlationId.trim());
    if (filters?.userId !== undefined && filters.userId > 0) q.set('user_id', String(filters.userId));
    if (filters?.startDate) q.set('start_date', filters.startDate);
    if (filters?.endDate) q.set('end_date', filters.endDate);
    const qs = q.toString();

    try {
      const res = await apiClient.get<unknown>(`/api/admin/activity-logs${qs ? `?${qs}` : ''}`);
      const r = res as unknown as Record<string, unknown>;
      const data = Array.isArray(r.data) ? (r.data as ActivityLog[]) : [];
      const pagination = r.pagination as ActivityLogsPagination | undefined;
      return {
        success: r.success !== false,
        data,
        pagination: pagination ?? { page: 1, limit: 25, total: data.length, lastPage: 1 },
        message: typeof r.message === 'string' ? r.message : undefined,
      };
    } catch (error) {
      return {
        success: false,
        data: [],
        pagination: { page: 1, limit: filters?.limit ?? 25, total: 0, lastPage: 1 },
        message: error instanceof Error ? error.message : 'Failed to fetch activity logs',
      };
    }
  }

  async getBusinessActivityLogs(filters?: BusinessLogFilters): Promise<{
    success: boolean;
    data: BusinessActivityLog[];
    pagination: ActivityLogsPagination;
    message?: string;
  }> {
    const q = new URLSearchParams();
    if (filters?.page) q.set('page', String(filters.page));
    if (filters?.limit) q.set('limit', String(filters.limit));
    if (filters?.module?.trim()) q.set('module', filters.module.trim());
    if (filters?.activityType?.trim()) q.set('activity_type', filters.activityType.trim());
    if (filters?.entityId?.trim()) q.set('entity_id', filters.entityId.trim());
    if (filters?.userId !== undefined && filters.userId > 0) q.set('user_id', String(filters.userId));
    if (filters?.correlationId?.trim()) q.set('correlation_id', filters.correlationId.trim());
    if (filters?.severity?.trim()) q.set('severity', filters.severity.trim());
    if (filters?.startDate) q.set('start_date', filters.startDate);
    if (filters?.endDate) q.set('end_date', filters.endDate);
    const qs = q.toString();

    try {
      const res = await apiClient.get<unknown>(`/api/admin/business-activity-logs${qs ? `?${qs}` : ''}`);
      const r = res as unknown as Record<string, unknown>;
      const data = Array.isArray(r.data) ? (r.data as BusinessActivityLog[]) : [];
      const pagination = r.pagination as ActivityLogsPagination | undefined;
      return {
        success: r.success !== false,
        data,
        pagination: pagination ?? { page: 1, limit: 25, total: data.length, lastPage: 1 },
        message: typeof r.message === 'string' ? r.message : undefined,
      };
    } catch (error) {
      return {
        success: false,
        data: [],
        pagination: { page: 1, limit: filters?.limit ?? 25, total: 0, lastPage: 1 },
        message: error instanceof Error ? error.message : 'Failed to fetch business activity logs',
      };
    }
  }

  async getAnalytics() {
    try {
      const response = await apiClient.get<any>('/api/provider/analytics/dashboard');

      if (response.success && response.data) {
        const data = response.data.data || response.data;
        return {
          success: true,
          data: {
            totalUsers: 0,
            totalProviders: 0,
            totalContent: data.contentMetrics?.views || 0,
            totalRevenue: data.revenue?.estimatedRevenue || 0,
            recentActivity: [],
          },
        };
      }

      throw new Error('Provider not found or analytics unavailable');
    } catch (error) {
      return {
        success: false,
        data: {
          totalUsers: 0,
          totalProviders: 0,
          totalContent: 0,
          totalRevenue: 0,
          recentActivity: [],
        },
        message: error instanceof Error ? error.message : 'Failed to fetch analytics',
      };
    }
  }

  async getContentAnalytics(contentId?: string) {
    const endpoint = contentId
      ? `/api/provider/analytics/content/${contentId}`
      : '/api/provider/analytics/content';
    return apiClient.get(endpoint);
  }
}

export const logsService = new LogsService();
